import React, {
    createContext,
    useContext,
    useRef,
    useEffect,
    useCallback,
    ReactNode,
    Dispatch,
    SetStateAction,
} from 'react';
import type { Song } from '@/types/types';
import type { YouTubePlayer } from 'react-youtube';
import { usePlayerStore } from '@/stores/playerStore';

interface PlayerContextType {
    song: Song;
    setSong: Dispatch<SetStateAction<Song>>;
    currentSong: Song | null;
    setCurrentSong: Dispatch<SetStateAction<Song | null>>;
    isPlaying: boolean;
    setIsPlaying: Dispatch<SetStateAction<boolean>>;
    currentTime: number;
    setCurrentTime: Dispatch<SetStateAction<number>>;
    duration: number;
    setDuration: Dispatch<SetStateAction<number>>;
    volume: number;
    setVolume: Dispatch<SetStateAction<number>>;
    isFullScreen: boolean;
    setIsFullScreen: Dispatch<SetStateAction<boolean>>;
    audioRef: React.RefObject<HTMLAudioElement>;
    autoPlayNext: boolean;
    setAutoPlayNext: Dispatch<SetStateAction<boolean>>;
    player: YouTubePlayer | null;
    setPlayer: Dispatch<SetStateAction<YouTubePlayer | null>>;
    progress: number;
    handleSeek: (percent: number) => void;
    transitionAudioRef: React.RefObject<HTMLAudioElement>;
    transitionAudioSrc: string;
    prepareTransitionAudio: (src: string) => Promise<boolean>;
    playTransitionAudio: (src: string, callbacks?: { onStarted?: () => void; onError?: () => void }) => void;
    setTransitionAudioVolume: (volume: number) => void;
    stopTransitionAudio: () => void;
    isTransitionPlaying: boolean;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        player, setPlayer, song, setSong, currentSong, setCurrentSong,
        isPlaying, setIsPlaying, currentTime, setCurrentTime, duration, setDuration,
        volume, setVolume, isFullScreen, setIsFullScreen, autoPlayNext, setAutoPlayNext,
        transitionAudioSrc, setTransitionAudioSrc, isTransitionPlaying, setIsTransitionPlaying,
    } = usePlayerStore();
    const audioRef = useRef<HTMLAudioElement>(null);
    const transitionAudioRef = useRef<HTMLAudioElement>(null);
    const transitionFadeFrameRef = useRef<number | null>(null);
    const preparedTransitionAudioRef = useRef(new Map<string, {
        audio: HTMLAudioElement;
        ready: Promise<boolean>;
    }>());
    const transitionPlaySequenceRef = useRef(0);
    const audioPlaySequenceRef = useRef(new WeakMap<HTMLAudioElement, number>());
    const transitionPlaybackCleanupRef = useRef<(() => void) | null>(null);
    const transitionVolumeRef = useRef(1);
    const transitionFadeGainRef = useRef(0);
    const setTransitionAudioVolume = useCallback((volume: number) => {
        transitionVolumeRef.current = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0;
        if (transitionAudioRef.current) transitionAudioRef.current.volume = transitionFadeGainRef.current * transitionVolumeRef.current;
    }, []);
    const audioSource = song.audioSrc || song.url || "";

    const fadeTransitionVolume = useCallback((from: number, to: number, durationMs: number, onComplete?: () => void) => {
        const audio = transitionAudioRef.current;
        if (!audio) return;
        if (transitionFadeFrameRef.current !== null) cancelAnimationFrame(transitionFadeFrameRef.current);
        const startedAt = performance.now();
        const tick = (now: number) => {
            const progress = Math.min(1, Math.max(0, (now - startedAt) / Math.max(1, durationMs)));
            transitionFadeGainRef.current = Math.max(0, Math.min(1, from + ((to - from) * progress)));
            audio.volume = transitionFadeGainRef.current * transitionVolumeRef.current;
            if (progress < 1) transitionFadeFrameRef.current = requestAnimationFrame(tick);
            else {
                transitionFadeFrameRef.current = null;
                onComplete?.();
            }
        };
        transitionFadeGainRef.current = Math.max(0, Math.min(1, from));
        audio.volume = transitionFadeGainRef.current * transitionVolumeRef.current;
        transitionFadeFrameRef.current = requestAnimationFrame(tick);
    }, []);

    const prepareTransitionAudio = useCallback((src: string): Promise<boolean> => {
        const normalizedSrc = String(src || "").trim();
        if (!normalizedSrc || typeof Audio === "undefined") return Promise.resolve(false);

        const existing = preparedTransitionAudioRef.current.get(normalizedSrc);
        if (existing) return existing.ready;

        const audio = new Audio();
        audio.preload = "auto";
        audio.src = normalizedSrc;

        const ready = new Promise<boolean>((resolve) => {
            let settled = false;
            const finish = (isReady: boolean) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeoutId);
                audio.removeEventListener("canplay", handleCanPlay);
                audio.removeEventListener("error", handleError);
                if (!isReady) {
                    preparedTransitionAudioRef.current.delete(normalizedSrc);
                }
                resolve(isReady);
            };
            const handleCanPlay = () => finish(true);
            const handleError = () => finish(false);
            const timeoutId = window.setTimeout(() => finish(false), 12000);

            audio.addEventListener("canplay", handleCanPlay, { once: true });
            audio.addEventListener("error", handleError, { once: true });
            audio.load();
            if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) finish(true);
        });

        preparedTransitionAudioRef.current.set(normalizedSrc, { audio, ready });
        setTransitionAudioSrc(normalizedSrc);
        return ready;
    }, [setTransitionAudioSrc]);

    const playTransitionAudio = useCallback((src: string, callbacks?: { onStarted?: () => void; onError?: () => void }) => {
        const normalizedSrc = String(src || "").trim();
        if (!normalizedSrc) return;
        const playSequence = ++transitionPlaySequenceRef.current;
        transitionPlaybackCleanupRef.current?.();

        void (async () => {
            // Preload opportunistically, but do not put the handoff behind a
            // readiness event. play() starts buffering and reports real errors.
            void prepareTransitionAudio(normalizedSrc);
            const prepared = preparedTransitionAudioRef.current.get(normalizedSrc);
            if (!prepared) { callbacks?.onError?.(); return; }
            if (playSequence !== transitionPlaySequenceRef.current) return;

            const audio = prepared.audio;
            audioPlaySequenceRef.current.set(audio, playSequence);
            if (transitionFadeFrameRef.current !== null) {
                cancelAnimationFrame(transitionFadeFrameRef.current);
                transitionFadeFrameRef.current = null;
            }
            const previousAudio = transitionAudioRef.current;
            if (previousAudio && previousAudio !== audio) {
                previousAudio.pause();
                previousAudio.currentTime = 0;
            }

            transitionAudioRef.current = audio;
            audio.loop = true;
            audio.currentTime = 0;
            transitionFadeGainRef.current = 0;
            audio.volume = 0;
            setTransitionAudioSrc(normalizedSrc);
            setIsTransitionPlaying(true);

            const reportFailure = () => {
                if (playSequence !== transitionPlaySequenceRef.current) return;
                transitionPlaySequenceRef.current += 1;
                transitionPlaybackCleanupRef.current?.();
                if (transitionFadeFrameRef.current !== null) cancelAnimationFrame(transitionFadeFrameRef.current);
                audio.pause();
                setIsTransitionPlaying(false);
                callbacks?.onError?.();
            };
            let started = false;
            const reportStarted = () => {
                if (started || playSequence !== transitionPlaySequenceRef.current) return;
                started = true;
                callbacks?.onStarted?.();
                fadeTransitionVolume(0, 1, 400);
            };
            audio.addEventListener("error", reportFailure, { once: true });
            audio.addEventListener("playing", reportStarted, { once: true });
            transitionPlaybackCleanupRef.current = () => {
                audio.removeEventListener("error", reportFailure);
                audio.removeEventListener("playing", reportStarted);
            };
            try {
                const playback = audio.play();
                // Older TV engines return void. Wait for their actual playing event.
                if (!playback || typeof playback.then !== "function") return;
                await playback;
                if (playSequence !== transitionPlaySequenceRef.current) {
                    if (audioPlaySequenceRef.current.get(audio) === playSequence) {
                        audio.pause();
                        audio.currentTime = 0;
                    }
                    return;
                }
                reportStarted();
            } catch (error) {
                if (playSequence !== transitionPlaySequenceRef.current) return;
                console.warn("Transition audio playback was blocked or failed:", error);
                reportFailure();
            }
        })();
    }, [fadeTransitionVolume, prepareTransitionAudio, setIsTransitionPlaying, setTransitionAudioSrc]);

    const stopTransitionAudio = useCallback(() => {
        transitionPlaySequenceRef.current += 1;
        transitionPlaybackCleanupRef.current?.();
        if (!transitionAudioRef.current) return;
        const audio = transitionAudioRef.current;
        fadeTransitionVolume(transitionFadeGainRef.current, 0, 800, () => {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = 1;
            setIsTransitionPlaying(false);
        });
    }, [fadeTransitionVolume, setIsTransitionPlaying]);

    useEffect(() => () => {
        transitionPlaySequenceRef.current += 1;
        transitionPlaybackCleanupRef.current?.();
        if (transitionFadeFrameRef.current !== null) cancelAnimationFrame(transitionFadeFrameRef.current);
        preparedTransitionAudioRef.current.forEach(({ audio }) => {
            audio.pause();
            audio.removeAttribute("src");
            audio.load();
        });
        preparedTransitionAudioRef.current.clear();
    }, [fadeTransitionVolume, setIsTransitionPlaying]);

    // ✅ computed progress
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    // ✅ fixed handleSeek
    const handleSeek = (percent: number) => {
        const newTime = duration * percent;
        if (audioRef.current) {
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
        if (player) {
            player.seekTo(newTime, true);
            setCurrentTime(newTime);
        }
    };

    // Update current time for audio
    useEffect(() => {
        const interval = setInterval(() => {
            if (audioRef.current) {
                setCurrentTime(audioRef.current.currentTime);
                setDuration(audioRef.current.duration || 0);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [setCurrentTime, setDuration]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying) {
                if (!audioSource) {
                    setIsPlaying(false);
                    return;
                }
                audioRef.current.play().catch(() => setIsPlaying(false));
            } else {
                audioRef.current.pause();
            }
        }

        if (player) {
            try {
                if (isPlaying) {
                    player.playVideo();
                } else {
                    player.pauseVideo();
                }
            } catch {
                // Ignore transient player state errors during mount/unmount.
            }
        }
    }, [audioSource, isPlaying, player, setIsPlaying]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.onended = () => {
                setIsPlaying(false);
            };
        }
    }, [setIsPlaying]);

    useEffect(() => {
        if (!player) return;

        const handleStateChange = (event: { data: number }) => {
            switch (event.data) {
                case 1: // Playing
                    setIsPlaying(true);
                    break;
                case 0: // Ended
                    setIsPlaying(false);
                    break;
                case 5: // Cued
                    if (isPlaying) player.playVideo();
                    break;
            }
        };

        player.addEventListener('onStateChange', handleStateChange);
        return () => player.removeEventListener('onStateChange', handleStateChange);
    }, [player, isPlaying, setIsPlaying]);

    return (
        <PlayerContext.Provider
            value={{
                song,
                setSong,
                currentSong,
                setCurrentSong,
                isPlaying,
                setIsPlaying,
                currentTime,
                setCurrentTime,
                duration,
                setDuration,
                volume,
                setVolume,
                isFullScreen,
                setIsFullScreen,
                audioRef,
                autoPlayNext,
                setAutoPlayNext,
                player,
                setPlayer,
                 progress,    // ✅ added
                 handleSeek,  // ✅ fixed
                 setTransitionAudioVolume,
                 transitionAudioRef,
                 transitionAudioSrc,
                 prepareTransitionAudio,
                 playTransitionAudio,
                 stopTransitionAudio,
                 isTransitionPlaying,
             }}
         >
             {children}
             <audio ref={audioRef} src={audioSource || undefined} hidden />
         </PlayerContext.Provider>
     );
 };

export const usePlayer = (): PlayerContextType => {
    const context = useContext(PlayerContext);
    if (!context) {
        throw new Error('usePlayer must be used within a PlayerProvider');
    }
    return context;
};
